import React, { useContext, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Rocket,
  Handshake,
  LayoutDashboard,
  Repeat,
  MessageCircle,
  Contact,
  Network,
  Copy,
  MessagesSquare,
  Phone,
  Users,
  Settings,
  Tag,
  Megaphone,
  CalendarClock,
  ShoppingBag,
  Image,
  Users2,
  Building2,
  Webhook,
  KanbanSquare,
  BellRing,
  MessageSquareText,
  Workflow,
  BarChart3,
  ChevronDown,
  Megaphone as Aviso,
  ListChecks,
  Building,
  Receipt,
  Package,
  Pill,
  Puzzle,
  Shield,
  Sparkles,
  Activity,
  MonitorPlay,
  CircleDollarSign,
  BadgePercent,
  Bot,
  Lock,
  LifeBuoy,
  Plug,
  SlidersHorizontal,
  Paintbrush,
  Quote,
  Thermometer,
  Radio,
  // Seção Redes sociais (Zernio) — Megaphone e BarChart3 já entram acima.
  AtSign,
  FileText,
  ClipboardList,
  MessageSquare,
  Star,
  Target,
  Settings2,
  LayoutTemplate,
  Link2,
} from "lucide-react";

import { cn } from "../lib/utils";
import api from "../services/api";
import { lerPreferencia, gravarPreferencia } from "../services/preferencias";
import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import useModules from "../hooks/useModules";
import usePermissions from "../hooks/usePermissions";
import { Can } from "../components/Can";
import { ITENS_DO_PLANO_STATUS } from "../helpers/planoStatus";

// O que vai à direita do nome: selo Beta/Novo, "Em breve" ou o cadeado. Usado
// pela lateral e pela barra de cima.
export function MarcaDoModulo({ selo, foraDoPlano, emBreve }) {
  return (
    <>
      {selo && !emBreve && (
        <span className="ml-auto shrink-0 rounded-full bg-primary/10 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-primary">
          {selo}
        </span>
      )}
      {emBreve ? (
        <span className="ml-auto shrink-0 rounded-full bg-muted px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
          Em breve
        </span>
      ) : (
        foraDoPlano && (
          <Lock className={cn("h-3 w-3 shrink-0", !selo && "ml-auto")} />
        )
      )}
    </>
  );
}

// "Em breve" não leva a lugar nenhum: a tela daria 403.
export const tituloDoItem = (label, foraDoPlano, emBreve) =>
  emBreve
    ? `${label} — em breve.`
    : foraDoPlano
      ? `${label} — não está no seu plano. Fale com quem administra a conta para liberar.`
      : undefined;

export function NavItem({
  to,
  icon: Icon,
  label,
  badge,
  onClick,
  collapsed,
  // Recurso fora do plano. Some do menu era pior do que aparecer apagado:
  // quem não vê o item não sabe que ele existe, e não tem o que pedir.
  foraDoPlano,
  selo,
  emBreve,
  exato,
}) {
  return (
    <NavLink
      to={to}
      // `exato` obriga o caminho a bater inteiro. Vale para o item cujo
      // endereço é prefixo de outro ("/zernio" e "/zernio/publicacoes"), que
      // sem isso fica aceso junto com o filho. Não pode ser o padrão: Tickets
      // aponta para "/tickets" e precisa continuar aceso em "/tickets/123".
      end={exato}
      onClick={emBreve ? (e) => e.preventDefault() : onClick}
      aria-disabled={emBreve || undefined}
      // Recolhido só sobra o ícone: sem nome acessível o link vira "link"
      // para o leitor de tela, e no mouse não há como saber o que é sem
      // clicar. O title devolve as duas coisas.
      title={tituloDoItem(label, foraDoPlano, emBreve) || (collapsed ? label : undefined)}
      aria-label={collapsed ? label : undefined}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
          "hover:bg-accent hover:text-accent-foreground",
          isActive ? "bg-primary/10 text-primary" : "text-muted-foreground",
          foraDoPlano && "opacity-50",
          emBreve && "cursor-default hover:bg-transparent"
        )
      }
    >
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
        <Icon className="h-[18px] w-[18px]" />
        {badge && (
          <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-destructive text-[9px] font-bold text-destructive-foreground">
            !
          </span>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="truncate">{label}</span>
          <MarcaDoModulo selo={selo} foraDoPlano={foraDoPlano} emBreve={emBreve} />
        </>
      )}
    </NavLink>
  );
}

function NavSection({ id, label, collapsed, fechadaPorPadrao, children }) {
  // O "v2" zera a preferência guardada uma vez. Configurações nascia fechada e
  // engolia Conexões, Filas e Usuários; quem já tinha o "fechado" salvo
  // continuaria sem ver mesmo depois da mudança. Depois deste reset, a escolha
  // de cada um volta a valer normalmente.
  const storageKey = `navSection:v2:${id}`;
  const [open, setOpen] = useState(() => {
    const stored = lerPreferencia(storageKey, null);
    // Hoje só a Gestão SaaS nasce fechada: são telas de dono do produto, não
    // do dia a dia de quem atende.
    if (stored === null) return !fechadaPorPadrao;
    return stored === true;
  });

  const toggle = (e) => {
    // não deixa o clique do cabeçalho fechar o drawer mobile
    e.stopPropagation();
    setOpen((prev) => {
      gravarPreferencia(storageKey, !prev);
      return !prev;
    });
  };

  if (collapsed) {
    return (
      <>
        <div className="my-2 h-px bg-border" />
        {children}
      </>
    );
  }

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between rounded-md px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-foreground"
      >
        <span>{label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            !open && "-rotate-90"
          )}
        />
      </button>
      {/* Recuo com guia vertical: mostra de relance onde um grupo começa e
          termina. Sem isso, título e itens ficavam no mesmo alinhamento e a
          lista parecia uma coluna única de 36 linhas soltas. */}
      <div
        className={cn(
          "ml-[18px] mt-0.5 flex flex-col gap-0.5 overflow-hidden border-l border-border/60 pl-1.5 transition-all",
          !open && "hidden"
        )}
      >
        {children}
      </div>
    </div>
  );
}

// O menu como dado, não como JSX.
//
// A barra de cima (TopNav) precisa exatamente dos mesmos itens, nas mesmas
// seções, com as mesmas permissões e os mesmos módulos do plano — só que em
// menus suspensos. Manter duas listas em dois arquivos garante que um dia elas
// divergem: entra uma tela nova na lateral e ela não existe no topo. Por isso
// a fonte é esta função; quem desenha apenas percorre o que ela devolve.
//
// Cada seção: { id, label, fechadaPorPadrao?, itens: [{ to, icon, label,
// badge?, foraDoPlano?, permissoes? }] }. Seção sem `label` é item solto, fora
// de grupo.
//
// `permissoes` é a lista que o backend exige para LER a tela, e basta uma —
// mesma regra do `can()` de lá. Item sem permissão nenhuma é escondido, não
// apagado como o fora do plano: fora do plano é algo a contratar, e falta de
// permissão é decisão do administrador da empresa — mostrar viraria um convite
// a clicar e levar 403.
export function useSecoesDoMenu() {
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  // Esconde do menu o que a empresa nao contratou.
  const { temModulo, exibicaoModulo, statusOnly } = useModules();
  // Esconde do menu o que o CARGO não abre. Enquanto as permissões não chegam,
  // `temPermissao` responde sim — o menu não pisca com itens sumindo, e quem
  // barra de verdade continua sendo o backend.
  const { temPermissao } = usePermissions();
  const [connectionWarning, setConnectionWarning] = useState(false);
  // A empresa é uma revenda? O backend responde `{ revenda: null }` para quem
  // não é (ou não pode ver o painel), sem 403 — uma pergunta por sessão.
  const [ehRevenda, setEhRevenda] = useState(false);
  // Idem para a sede de uma rede: `{ rede: null }` para quem não é.
  const [rede, setRede] = useState(null);

  useEffect(() => {
    if (!user?.id || Number(user.companyId) === 1) return;
    api
      .get("/revenda/me")
      .then(({ data }) => setEhRevenda(Boolean(data?.revenda)))
      .catch(() => setEhRevenda(false));
    api
      .get("/rede/me")
      .then(({ data }) => setRede(data?.rede || null))
      .catch(() => setRede(null));
  }, [user?.id, user?.companyId]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        setConnectionWarning(offlineWhats.length > 0);
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  // `Can` é um componente, mas é função pura sem hook: chamada direta ela
  // devolve o booleano que precisamos aqui. É a mesma regra de sempre
  // (src/rules), sem uma segunda checagem de permissão para manter em dia.
  const podeAdministrar = Can({
    role: user.profile,
    perform: "drawer-admin-items:view",
    yes: () => true,
    no: () => false,
  });

  // Plano "só Status": o menu é a lista fixa do produto, sem grupos.
  if (statusOnly) {
    return [
      {
        id: "status",
        label: null,
        itens: ITENS_DO_PLANO_STATUS.map((item) =>
          item.to === "/connections"
            ? { ...item, badge: connectionWarning }
            : item
        ),
      },
    ];
  }

  const secoes = [];

  // Dashboard sozinho no topo: é a tela inicial, não pertence a nenhum grupo.
  secoes.push({
    id: "inicio",
    label: null,
    itens: [{ to: "/", icon: LayoutDashboard, label: "Dashboard" }],
  });

  // Atendimento: a conversa, o contato e o material que a equipe reusa
  // ao responder.
  secoes.push({
    id: "atendimento",
    label: "Atendimento",
    itens: [
      {
        to: "/tickets",
        icon: MessageCircle,
        label: i18n.t("mainDrawer.listItems.tickets"),
      },
      {
        to: "/contacts",
        icon: Contact,
        label: i18n.t("mainDrawer.listItems.contacts"),
      },
      {
        to: "/organizations",
        icon: Building2,
        label: "Empresas",
      },
      // Chamada de voz mora em Atendimento porque é o mesmo trabalho da
      // conversa, por outro meio. O módulo `calls` é vendido à parte.
      {
        to: "/chamadas",
        icon: Phone,
        label: "Chamadas",
        modulo: "calls",
        permissoes: ["calls:use"],
      },
      {
        to: "/quickAnswers",
        icon: MessagesSquare,
        label: i18n.t("mainDrawer.listItems.quickAnswers"),
      },
      {
        to: "/media-library",
        icon: Image,
        label: "Biblioteca de mídias",
        modulo: "mediaLibrary",
      },
    ],
  });

  // Automação: tudo que fala com o cliente sem ninguém digitar.
  // As mensagens agendadas vieram para cá — elas são envio automático,
  // não material de apoio.
  // Facebook e Instagram estão fora do menu por enquanto: o token da Meta
  // expirou e o app ainda não tem o App Secret para renovar. A tela e a
  // integração continuam no código — é só devolver o item e a rota em
  // routes/index.js quando as credenciais estiverem prontas.
  secoes.push({
    id: "automacao",
    label: "Automação",
    itens: [
      {
        to: "/flows",
        icon: Workflow,
        label: "Fluxo de conversa",
        modulo: "flows",
        permissoes: ["system:integrations"],
      },
      {
        to: "/scheduled-messages",
        icon: CalendarClock,
        label: "Agendamentos",
        modulo: "schedules",
      },
      // "Status", o nome que o próprio WhatsApp usa. Nasceu como "Recados" e
      // ninguém procurava por isso no menu.
      {
        to: "/status-posts",
        icon: Radio,
        label: "Status",
      },
      {
        to: "/ai",
        icon: Sparkles,
        label: "Talk.Ai",
        modulo: "ai",
        permissoes: ["ai:models"],
      },
    ],
  });

  // Vender: prospecção e disparo em massa moram juntos porque a campanha
  // é a ferramenta de quem trabalha os leads.
  secoes.push({
    id: "vendas",
    label: "Vendas",
    itens: [
      // Leads, funil e tarefas são o mesmo trabalho visto de três ângulos:
      // moram juntos no Kanban, em abas.
      {
        to: "/kanban",
        icon: KanbanSquare,
        label: "Kanban",
        // Quem barra /kanban no backend é o módulo "kanban" (ModuleKeys.ts).
        // O menu segue a mesma chave para não mostrar botão de rota bloqueada.
        modulo: "kanban",
      },
      // O catálogo mora em Vendas e não em Atendimento: quem cuida de preço é
      // quem vende, e é do catálogo que saem o disparo pela conversa e o item
      // do orçamento.
      {
        to: "/products",
        icon: Package,
        label: "Catálogo",
        // Quem barra /products no backend é o módulo "erp" (ModuleKeys.ts).
        modulo: "erp",
      },
      // ERP de farmácia: sincroniza o catálogo e recebe o pedido. Vendido à
      // parte (módulo "farmax"); a configuração é do administrador.
      {
        to: "/farmax",
        icon: Pill,
        label: "Farmax",
        modulo: "farmax",
        permissoes: ["system:integrations"],
      },
      // Orçamentos, propostas, pedidos e locações eram quatro itens soltos
      // sem dizer como um leva ao outro — e a compra da loja caía num deles
      // sem ninguém ver. Viraram um item só: a central, que mostra o que
      // precisa de gente agora e leva a cada etapa pelas abas do topo. As
      // rotas antigas continuam valendo (links, avisos, favoritos).
      {
        to: "/vendas",
        icon: ShoppingBag,
        label: "Vendas",
        modulo: ["erp", "propostas"],
      },
      // Modelos de documento saiu daqui: é configuração de aparência de PDF,
      // não tela de operação diária — virou cartão em Configurações → Vendas.
      // O follow-up existe por causa do que foi enviado e não voltou: mora
      // junto de quem envia.
      {
        to: "/follow-ups",
        icon: BellRing,
        label: "Follow-ups",
        modulo: "followUp",
      },
    ],
  });

  // Disparo em massa é outro trabalho: quem opera a campanha não é quem
  // acompanha o ciclo de uma venda — misturar os dois numa lista de 11 itens
  // era o que fazia o menu não se deixar escanear.
  secoes.push({
    id: "campanhas",
    label: "Campanhas",
    itens: [
      {
        to: "/campaigns",
        icon: Megaphone,
        label: "Campanhas",
        modulo: "campaigns",
      },
      {
        to: "/contact-lists",
        icon: ListChecks,
        label: "Listas de Contatos",
        modulo: "campaigns",
      },
      {
        to: "/campaigns-phrases",
        icon: Quote,
        // O nome antigo ("Fluxo de Campanha") não dizia o que a tela guarda, e
        // a tela é também o que a maturação de chips exige para iniciar — por
        // isso ela aparece para quem tem qualquer um dos dois módulos.
        label: "Listas de frases",
        modulo: ["campaigns", "maturation"],
      },
      {
        to: "/maturacao",
        icon: Thermometer,
        label: "Maturação de chips",
        modulo: "maturation",
      },
      {
        to: "/campaigns-config",
        icon: SlidersHorizontal,
        label: "Config. de campanhas",
        modulo: "campaigns",
      },
    ],
  });

  // Acompanhar: os números do que já aconteceu.
  secoes.push({
    id: "analise",
    label: "Acompanhamento",
    itens: [
      {
        to: "/operational-panel",
        icon: Activity,
        label: "Painel",
        permissoes: ["dashboard:metrics", "supervision:monitor"],
      },
      {
        to: "/monitor",
        icon: MonitorPlay,
        label: "Monitor de atendimentos",
        permissoes: ["dashboard:metrics", "supervision:monitor"],
      },
      {
        to: "/reports",
        icon: BarChart3,
        label: "Relatórios",
        modulo: "reports",
      },
    ],
  });

  // Equipe: conversa interna, não com o cliente.
  secoes.push({
    id: "equipe",
    label: "Equipe",
    itens: [
      {
        to: "/internal-chat",
        icon: MessageSquareText,
        label: "Chat Interno",
        modulo: "internalChat",
      },
      { to: "/announcements", icon: Aviso, label: "Informativos" },
      { to: "/helps", icon: LifeBuoy, label: "Ajuda" },
    ],
  });

  // Redes sociais: tudo que passa pela Zernio. Seção própria porque são sete
  // trabalhos diferentes — atender, publicar, responder comentário, responder
  // avaliação — e não variações de uma tela só.
  // Redes sociais é módulo vendido à parte: quem não o contratou vê os itens
  // com cadeado, como acontece com campanhas e fluxos.

  secoes.push({
    id: "zernio",
    label: "Redes sociais",
    fechadaPorPadrao: true,
    itens: [
      // `exato` porque "/zernio" é prefixo das outras seis.
      {
        to: "/zernio",
        icon: Plug,
        label: "Contas",
        exato: true,
        modulo: "zernio",
      },
      {
        to: "/zernio/publicacoes",
        icon: Megaphone,
        label: "Publicações",
        modulo: "zernio",
      },
      {
        to: "/zernio/comentarios",
        icon: MessageSquare,
        label: "Comentários",
        modulo: "zernio",
      },
      {
        to: "/zernio/mencoes",
        icon: AtSign,
        label: "Menções",
        modulo: "zernio",
      },
      {
        to: "/zernio/avaliacoes",
        icon: Star,
        label: "Avaliações",
        modulo: "zernio",
      },
      {
        to: "/zernio/analises",
        icon: BarChart3,
        label: "Análises",
        modulo: "zernio",
      },
      {
        to: "/zernio/anuncios",
        icon: Target,
        label: "Anúncios",
        modulo: "zernio",
      },
      // A Configuração deixou de ser só da operadora: a empresa liberada para
      // usar conta própria grava a chave dela ali, e quem não foi liberado vê
      // na mesma tela de onde vem a conta que está usando.
      {
        to: "/zernio/configuracao",
        icon: Settings2,
        label: "Configuração",
        modulo: "zernio",
      },
    ],
  });

  // Configurações: o que se ajusta uma vez e esquece. Nasce fechada —
  // são nove linhas que empurravam o dia a dia para fora da tela.
  // Conexões saiu do topo: é configuração, mesmo sendo importante.
  if (podeAdministrar) {
    secoes.push({
      id: "configuracoes",
      label: "Configurações",
      itens: [
        {
          to: "/connections",
          icon: Repeat,
          label: i18n.t("mainDrawer.listItems.connections"),
          badge: connectionWarning,
        },
        // Da conexão de disparo para a de conversa: mora junto das conexões
        // porque é entre elas que o link leva o cliente.
        {
          to: "/links-de-redirecionamento",
          icon: Link2,
          label: "Links de redirecionamento",
          permissoes: ["connections:manage"],
        },
        {
          to: "/Queues",
          icon: Network,
          label: i18n.t("mainDrawer.listItems.queues"),
        },
        { to: "/tags", icon: Tag, label: i18n.t("mainDrawer.listItems.tags") },
        {
          to: "/users",
          icon: Users,
          label: i18n.t("mainDrawer.listItems.users"),
        },
        {
          to: "/roles",
          icon: Shield,
          label: "Cargos e permissões",
          permissoes: ["users:roles"],
        },
        { to: "/teams", icon: Users2, label: "Equipes" },
        {
          to: "/webhooks",
          icon: Webhook,
          label: "Webhooks",
          modulo: "webhooks",
        },
        { to: "/queue-integrations", icon: Bot, label: "Integrações" },
        // Modelos do WhatsApp oficial: sem eles não há campanha nem retomada
        // de conversa passadas 24 h.
        { to: "/modelos-whatsapp", icon: FileText, label: "Modelos WhatsApp" },
        {
          to: "/messages-api",
          icon: Plug,
          label: "API",
          // A tela documenta e testa a API externa, cujas rotas (/api) o
          // backend barra sem o módulo. Sem isto ela aparecia normal e o
          // primeiro sinal de que não está no plano era um 403 no teste.
          modulo: "externalApi",
        },
        // Ajustes da plataforma inteira: a rota é isSuperAdmin, então
        // mostrar para o cliente só renderia um 403.
        ...(Number(user.companyId) === 1
          ? [
              {
                to: "/global-config",
                icon: SlidersHorizontal,
                label: "Configurações globais",
              },
              {
                to: "/estilos-visuais",
                icon: Paintbrush,
                label: "Estilos visuais",
              },
            ]
          : []),
        // A empresa operadora já tem o painel financeiro do SaaS; para ela
        // esta tela seria a própria fatura, que não existe.
        ...(Number(user.companyId) !== 1
          ? [
              {
                to: "/financeiro",
                icon: CircleDollarSign,
                label: "Financeiro",
              },
            ]
          : []),
        {
          to: "/Settings",
          icon: Settings,
          label: i18n.t("mainDrawer.listItems.settings"),
        },
      ],
    });
  }

  // Quem não administra só tem "Minha conta" (tela, notificações, atalhos,
  // 2FA): a página esconde o que é da empresa.
  if (!podeAdministrar) {
    secoes.push({
      id: "configuracoes",
      label: "Configurações",
      itens: [{ to: "/Settings", icon: Settings, label: "Minha conta" }],
    });
  }

  // Painel da revenda: só a empresa que é uma revenda, para quem pode vê-lo.
  if (ehRevenda) {
    secoes.push({
      id: "revenda",
      label: "Revenda",
      itens: [
        // `exato`: "/revenda" é prefixo dos outros dois.
        { to: "/revenda", icon: Handshake, label: "Meus clientes", exato: true },
        { to: "/revenda/implantar", icon: Rocket, label: "Implantar cliente" },
        { to: "/revenda/formularios", icon: ClipboardList, label: "Formulários de implantação" },
      ],
    });
  }

  // Painel da rede: só a sede de uma rede, e cada item pela sua capacidade
  // (`/rede/me` vem para quem tem qualquer uma delas).
  if (rede) {
    secoes.push({
      id: "rede",
      label: "Rede",
      itens: [
        // O servidor confere de novo; aqui só some o que a pessoa não pode usar.
        // `exato`: "/rede" é prefixo do monitor e do padronizar.
        ...(rede.pode?.includes("rede:view")
          ? [{ to: "/rede", icon: Network, label: "Desempenho das filiais", exato: true }]
          : []),
        ...(rede.pode?.includes("rede:conversas")
          ? [{ to: "/rede/monitor", icon: MonitorPlay, label: "Monitor das filiais" }]
          : []),
        ...(rede.pode?.includes("rede:padronizar")
          ? [{ to: "/rede/padronizar", icon: Copy, label: "Padronizar filiais" }]
          : []),
      ],
    });
  }

  // Gestão do SaaS: só o admin da empresa operadora (id 1) enxerga.
  if (user.profile === "admin" && Number(user.companyId) === 1) {
    secoes.push({
      id: "saas",
      label: "Gestão SaaS",
      fechadaPorPadrao: true,
      itens: [
        { to: "/saas/finance", icon: CircleDollarSign, label: "Painel financeiro" },
        { to: "/companies", icon: Building, label: "Empresas" },
        { to: "/onboarding", icon: Rocket, label: "Implantar cliente" },
        { to: "/onboarding-forms", icon: ClipboardList, label: "Formulários de implantação" },
        { to: "/plans", icon: Package, label: "Planos" },
        { to: "/modules", icon: Puzzle, label: "Módulos" },
        { to: "/flow-templates", icon: LayoutTemplate, label: "Modelos de fluxo" },
        { to: "/saas/commercial", icon: BadgePercent, label: "Comercial" },
        { to: "/saas/redes", icon: Network, label: "Redes" },
        { to: "/invoices", icon: Receipt, label: "Faturas" },
      ],
    });
  }

  // Um lugar só para tirar do menu o que o cargo não abre, e a seção que ficou
  // sem nenhum item sai junto — título de grupo vazio é pior que nada.
  // `modulo` (chave ou lista — basta uma) vira o estado do item: cadeado,
  // "Em breve", escondido e o selo Beta/Novo, conforme o super admin definiu.
  const comModulo = (item) => {
    if (!item.modulo) return item;
    const chaves = [].concat(item.modulo);
    const contratada = chaves.find(temModulo);
    const { stage, lockedDisplay } = exibicaoModulo(contratada || chaves[0]);
    return {
      ...item,
      selo: stage === "beta" ? "Beta" : stage === "new" ? "Novo" : null,
      foraDoPlano: !contratada,
      emBreve: !contratada && lockedDisplay === "soon",
      oculto: !contratada && lockedDisplay === "hidden",
    };
  };

  return secoes
    .map(secao => ({
      ...secao,
      itens: secao.itens
        .map(comModulo)
        .filter(
          item =>
            !item.oculto &&
            (!item.permissoes || item.permissoes.some(temPermissao))
        ),
    }))
    .filter(secao => secao.itens.length > 0);
}

const MainListItems = ({ drawerClose, collapsed }) => {
  const secoes = useSecoesDoMenu();

  return (
    <nav className="flex flex-col gap-0.5 px-2 pb-6" onClick={drawerClose}>
      {secoes.map((secao) =>
        secao.label ? (
          <NavSection
            key={secao.id}
            id={secao.id}
            label={secao.label}
            collapsed={collapsed}
            fechadaPorPadrao={secao.fechadaPorPadrao}
          >
            {secao.itens.map((item) => (
              <NavItem key={item.to} {...item} collapsed={collapsed} />
            ))}
          </NavSection>
        ) : (
          secao.itens.map((item) => (
            <NavItem key={item.to} {...item} collapsed={collapsed} />
          ))
        )
      )}
    </nav>
  );
};

export default MainListItems;
